import { Header } from "@/components/Header";
import { CompactFilterBar } from "@/components/CompactFilterBar";
import { RecallCard, type Recall as RecallCardType } from "@/components/RecallCard";
import { RecallCardSkeleton } from "@/components/RecallCardSkeleton";
import { DateGroupHeader } from "@/components/DateGroupHeader";
import { SearchWithSuggestions } from "@/components/SearchWithSuggestions";
import { useSmartFilters } from "@/hooks/useSmartFilters";

import { useRecallsPaginated } from "@/hooks/useRecallsPaginated";
import { useBarcodeSearch } from "@/hooks/useBarcodeSearch";
import { isBarcodeQuery } from "@/lib/recallUtils";
import { useUserRole } from "@/hooks/useUserRole";
import { useRealtimeRecalls } from "@/hooks/useRealtimeRecalls";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { startOfDay } from "date-fns";
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEOHead } from "@/components/SEOHead";
import { getSeverityLevelByRiskType } from "@/lib/recallUtils";


export default function Index() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useUserRole();
  
  // Scroll to specific recall card when coming back from detail page
  const scrollToRecallId = location.state?.scrollToRecallId;
  
  useEffect(() => {
    if (scrollToRecallId) {
      // Small delay to ensure DOM is ready after data loads
      const timer = setTimeout(() => {
        const element = document.querySelector(`[data-recall-id="${scrollToRecallId}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        // Clear state to prevent re-scroll on subsequent renders
        window.history.replaceState({}, '');
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [scrollToRecallId]);
  const [showDebug, setShowDebug] = useState(false);
  const [filters, setFilters] = useState({
    category: 'all' as 'all' | 'baby-food',
    categories: [] as string[],
    bacteria: [] as string[],
    riskType: 'all' as 'all' | 'mycotoxine_aflatoxine' | 'mycotoxine_ochratoxine' | 'pesticide_eto' | 'chimique_moah_pah' | 'conditionnement',
    riskCategory: 'all' as 'all' | 'pathogen' | 'chemical' | 'allergen' | 'packaging' | 'other',
    severity: 'all' as 'all' | 'high' | 'medium' | 'low',
    pregnancyOnly: false,
    retailers: [] as string[], // Multi-select retailers
    regions: [] as string[], // Multi-select regions
    agents: [] as string[],
    search: '',
    period: '30d' as '7d' | '30d' | '90d'
  });

  const { smartPreset } = useSmartFilters();
  const { newRecallsCount, dismissNewRecalls } = useRealtimeRecalls();
  
  // Debounced search for performance
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search || '');
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Recherche textuelle : côté serveur, période élargie automatiquement à 90 j
  const trimmedSearch = debouncedSearch.trim();
  const textSearch = !isBarcodeQuery(trimmedSearch) && trimmedSearch.length >= 2 ? trimmedSearch : '';
  const effectivePeriod = textSearch ? '90d' as const : filters.period;

  // Fetch recalls with React Query infinite pagination
  const { 
    data, 
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useRecallsPaginated({
    category: filters.category,
    bacteria: filters.bacteria,
    riskType: filters.riskType,
    riskCategory: filters.riskCategory,
    severity: filters.severity,
    pregnancyOnly: filters.pregnancyOnly,
    retailers: filters.retailers,
    regions: filters.regions,
    agents: filters.agents,
    categories: filters.categories,
    period: effectivePeriod,
    search: textSearch,
  });

  // Flatten pages into a single array
  const recalls = useMemo(() => 
    data?.pages.flatMap(page => page.recalls) || [], 
    [data]
  );
  const totalCount = data?.pages[0]?.totalCount || 0;
  const remainingCount = totalCount - recalls.length;

  // Get last update time
  const lastUpdate = queryClient.getQueryState(['recalls-paginated', {
    category: filters.category,
    bacteria: filters.bacteria,
    riskType: filters.riskType,
    riskCategory: filters.riskCategory,
    severity: filters.severity,
    pregnancyOnly: filters.pregnancyOnly,
    retailers: filters.retailers,
    regions: filters.regions,
    agents: filters.agents,
    categories: filters.categories,
    period: effectivePeriod,
    search: textSearch,
  }])?.dataUpdatedAt;

  // Apply smart preset to filters when available
  useEffect(() => {
    if (smartPreset?.isActive && smartPreset.filters) {
      const updates: Partial<typeof filters> = {};
      if (smartPreset.filters.category) updates.category = smartPreset.filters.category;
      if (smartPreset.filters.pregnancyOnly !== undefined) updates.pregnancyOnly = smartPreset.filters.pregnancyOnly;
      if (smartPreset.filters.severity) updates.severity = smartPreset.filters.severity;
      
      setFilters(prev => ({
        ...prev,
        ...updates
      }));
    }
  }, [smartPreset]);



  // Transform recalls for RecallCard component
  type RawRecall = (typeof recalls)[number];
  const transformRecall = (recall: RawRecall) => ({
      id: recall.id,
      productName: recall.product_name,
      brand: recall.brand,
      riskType: recall.risk_type as RecallCardType['riskType'],
      severity: getSeverityLevelByRiskType(recall.risk_type),
      pregnancyFlag: recall.pregnancy_flag,
      batchLot: recall.batch_lot || undefined,
      dlc: recall.dlc_ddm || undefined,
      distributionArea: recall.distribution_area,
      retailer: recall.retailer,
      actionRequired: recall.action_required || undefined,
      motive: (recall as { motive?: string }).motive || undefined,
      publishedAt: recall.published_at,
      category: recall.category_id === '25' ? 'baby-food' : 'other',
      category_id: recall.category_id,
      applicability: recall.applicability,
      images: recall.images,
      gtin: recall.gtin || undefined,
      officialUrl: recall.official_url || undefined,
      source_url: recall.source_url || undefined
    });
  const transformedRecalls = useMemo(() => recalls.map(transformRecall), [recalls]);


  // Recherche code-barres : toute la base, sans limite de période
  const barcode = useBarcodeSearch(debouncedSearch);
  const oldestLabel = barcode.oldestDate
    ? new Date(barcode.oldestDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  // Apply debounced search filter
  const searchFilteredRecalls = useMemo(() => {
    if (barcode.isBarcode) return barcode.recalls.map(r => transformRecall(r as unknown as RawRecall));
    if (!debouncedSearch.trim()) return transformedRecalls;
    
    // Filtrage déjà fait côté serveur (sur 90 jours)
    return transformedRecalls;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transformedRecalls, debouncedSearch, barcode.isBarcode, barcode.recalls]);

  // Prefetch les 5 premiers rappels pour navigation instantanée.
  // On injecte la ligne complète de la base (même forme que useRecallDetail),
  // jamais un objet reconstruit partiel (sinon motif, grossesse… manquent).
  useEffect(() => {
    recalls.slice(0, 5).forEach(recall => {
      if (!queryClient.getQueryData(['recall', recall.id])) {
        queryClient.setQueryData(['recall', recall.id], recall);
      }
    });
  }, [recalls, queryClient]);

  // Group recalls by day
  const groupedRecalls = useMemo(() => {
    const groups = new Map<string, typeof searchFilteredRecalls>();
    
    searchFilteredRecalls.forEach(recall => {
      const dayKey = startOfDay(new Date(recall.publishedAt)).toISOString();
      if (!groups.has(dayKey)) {
        groups.set(dayKey, []);
      }
      groups.get(dayKey)!.push(recall);
    });
    
    // Sort groups by date (newest first)
    return Array.from(groups.entries())
      .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime());
  }, [searchFilteredRecalls]);

  const activeFiltersCount = [
    filters.category !== 'all',
    (filters.categories || []).length > 0,
    (filters.bacteria && filters.bacteria.length > 0),
    filters.riskType !== 'all',
    filters.riskCategory !== 'all',
    filters.severity !== 'all',
    filters.pregnancyOnly,
    (filters.retailers || []).length > 0,
    filters.regions.length > 0, // Multi-region filter
    filters.search && filters.search.trim() !== '',
    // Compter agents seulement si mode grossesse inactif
    !filters.pregnancyOnly && filters.agents.length > 0
  ].filter(Boolean).length;

  const resetFilters = () => {
    setFilters({
      category: 'all',
      categories: [],
      bacteria: [],
      riskType: 'all',
      riskCategory: 'all',
      severity: 'all',
      pregnancyOnly: false,
      retailers: [],
      regions: [],
      agents: [],
      search: '',
      period: '30d'
    });
  };

  const homeJsonLd = useMemo(() => [
    {
      "@type": "CollectionPage",
      "@id": "https://www.miamsecur.com/#collection",
      "name": "Rappels alimentaires officiels en France",
      "url": "https://www.miamsecur.com/",
      "inLanguage": "fr-FR",
      "description": "Flux des rappels de produits alimentaires publiés par RappelConso, avec analyse des risques grossesse et bébé.",
      "isPartOf": { "@id": "https://www.miamsecur.com/#website" },
      "publisher": { "@id": "https://www.miamsecur.com/#organization" },
      "mainEntity": {
        "@type": "ItemList",
        "name": "Derniers rappels alimentaires",
        "numberOfItems": Math.min(recalls.length, 20),
        "itemListOrder": "https://schema.org/ItemListOrderDescending",
        "itemListElement": recalls.slice(0, 20).map((recall, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": recall.product_name,
          "url": `https://www.miamsecur.com/recall/${recall.id}`
        }))
      }
    },

    {
      "@type": "WebSite",
      "@id": "https://www.miamsecur.com/#website",
      "name": "MiamSécur",
      "url": "https://www.miamsecur.com/",
      "inLanguage": "fr-FR",
      "description": "Alertes officielles de rappels alimentaires en France, avec analyse des risques grossesse et bébé.",
      "publisher": { "@id": "https://www.miamsecur.com/#organization" },
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": "https://www.miamsecur.com/?search={search_term_string}"
        },
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@type": "Organization",
      "@id": "https://www.miamsecur.com/#organization",
      "name": "MiamSécur",
      "url": "https://www.miamsecur.com/",
      "logo": "https://www.miamsecur.com/icon-512.png"
    },
    {
      "@type": "WebApplication",
      "name": "MiamSécur",
      "url": "https://www.miamsecur.com/",
      "applicationCategory": "HealthApplication",
      "operatingSystem": "Web",
      "inLanguage": "fr-FR",
      "description": "Suivi des rappels alimentaires officiels RappelConso avec filtres pathogènes, allergènes, régions et enseignes.",
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "EUR" },
      "publisher": { "@id": "https://www.miamsecur.com/#organization" }
    }
  ], [recalls]);



  return (
    <div className="min-h-screen bg-background">
      <SEOHead jsonLd={homeJsonLd} />

      <Header />
      
      {/* Bandeau nouveaux rappels en temps réel */}
      {newRecallsCount > 0 && (
        <div className="sticky top-0 z-50 bg-primary text-primary-foreground border-b">
          <div className="max-w-4xl mx-auto px-4 py-2 text-center">
            <Button 
              variant="secondary"
              size="sm"
              onClick={dismissNewRecalls}
              className="animate-pulse"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {newRecallsCount} nouveau{newRecallsCount > 1 ? 'x' : ''} rappel{newRecallsCount > 1 ? 's' : ''} - Actualiser
            </Button>
          </div>
        </div>
      )}
      
      <CompactFilterBar
        filters={filters}
        onFiltersChange={setFilters}
        activeFiltersCount={activeFiltersCount}
        totalCount={totalCount}
        isLoading={isLoading}
      />

      {/* Barre de recherche principale */}
      <div className="max-w-4xl mx-auto px-4 py-3">
        <SearchWithSuggestions
          value={filters.search}
          onChange={(value) => setFilters(prev => ({ ...prev, search: value }))}
          onSearch={() => {}}
          recalls={recalls}
          placeholder="Rechercher un produit ou une marque..."
        />
        
        {/* Indicateur de résultats de recherche */}
        {barcode.isBarcode ? (
          <p className="text-sm text-muted-foreground mt-2 text-center" role="status">
            {barcode.isLoading
              ? `Recherche du code-barres ${barcode.code} dans toute la base…`
              : searchFilteredRecalls.length === 0
                ? `Aucun rappel trouvé pour le code-barres ${barcode.code} dans toute la base MiamSécur${oldestLabel ? ` (rappels publiés depuis le ${oldestLabel})` : ''}.`
                : `Code-barres ${barcode.code} : ${searchFilteredRecalls.length} rappel${searchFilteredRecalls.length > 1 ? 's' : ''} trouvé${searchFilteredRecalls.length > 1 ? 's' : ''} dans toute la base${oldestLabel ? ` (depuis le ${oldestLabel})` : ''}, toutes périodes et filtres confondus.`}
          </p>
        ) : debouncedSearch.trim() && (
          <p className="text-sm text-muted-foreground mt-2 text-center">
            {searchFilteredRecalls.length === 0 
              ? `Aucun résultat pour "${debouncedSearch}" sur les 90 derniers jours`
              : `${searchFilteredRecalls.length} rappel${searchFilteredRecalls.length > 1 ? 's' : ''} trouvé${searchFilteredRecalls.length > 1 ? 's' : ''} pour "${debouncedSearch}" sur les 90 derniers jours`
            }
          </p>
        )}
      </div>

      <main className="container mx-auto px-4 py-6 max-w-7xl">
        <h1 className="sr-only">
          Rappels de produits alimentaires en France — alertes bébé, grossesse et famille
        </h1>


        {/* Debug section - Admin only */}
        {isAdmin && (
          <div className="mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDebug(!showDebug)}
              className="w-full justify-between"
            >
              <span className="flex items-center gap-2">
                🔍 Informations techniques
              </span>
              {showDebug ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
            
            {showDebug && (
              <div className="mt-2 p-4 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded-lg text-sm space-y-3">
                <div>
                  <div className="font-semibold text-yellow-900 dark:text-yellow-200 mb-2">
                    Métriques du feed
                  </div>
                  <div className="space-y-1 text-xs text-yellow-800 dark:text-yellow-300">
                    <div>Rappels bruts: {recalls.length}</div>
                    <div>Après filtres: {transformedRecalls.length}</div>
                    <div>Filtres actifs: {activeFiltersCount}</div>
                    <div>Catégorie: {filters.category}</div>
                    <div>Enseignes: {(filters.retailers || []).length > 0 ? filters.retailers.join(', ') : 'Toutes'}</div>
                    <div>Régions: {filters.regions.length > 0 ? filters.regions.join(', ') : 'Toutes'}</div>
                    <div>Source: API V2 (RappelConso)</div>
                    {lastUpdate && (
                      <div>Dernière mise à jour: {new Date(lastUpdate).toLocaleString('fr-FR')}</div>
                    )}
                  </div>
                </div>
                
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => navigate('/admin')}
                  className="p-0 h-auto text-yellow-900 dark:text-yellow-200"
                >
                  → Voir plus de métriques sur /admin
                </Button>
              </div>
            )}
          </div>
        )}

        {isLoading || barcode.isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <RecallCardSkeleton key={i} />
            ))}
          </div>
        ) : isError ? (
          <div className="text-center py-20 px-4 space-y-4" role="alert">
            <div className="text-6xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-foreground">
              Impossible de charger les rappels
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Une erreur est survenue lors de la récupération des alertes. Vérifie ta connexion puis réessaie.
            </p>
            {error instanceof Error && (
              <p className="text-xs text-muted-foreground/70 max-w-lg mx-auto break-words">
                {error.message}
              </p>
            )}
            <div className="pt-4">
              <Button variant="outline" size="lg" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Réessayer
              </Button>
            </div>
          </div>
        ) : barcode.isBarcode && searchFilteredRecalls.length === 0 ? (
          <div className="text-center py-20 px-4 space-y-4">
            <div className="text-6xl mb-4">✅</div>
            <h2 className="text-xl font-semibold text-foreground">
              Aucun rappel pour ce code-barres
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Le code {barcode.code} n'apparaît dans aucun rappel de la base MiamSécur
              {oldestLabel ? `, qui couvre les rappels publiés depuis le ${oldestLabel}` : ''}.
              Les rappels plus anciens ne sont pas vérifiés : en cas de doute, consultez rappel.conso.gouv.fr.
            </p>
            <div className="pt-4">
              <Button variant="outline" size="lg" onClick={() => setFilters(prev => ({ ...prev, search: '' }))}>
                Effacer la recherche
              </Button>
            </div>
          </div>
        ) : searchFilteredRecalls.length === 0 ? (
          <div className="text-center py-20 px-4 space-y-4">
            <div className="text-6xl mb-4">🔍</div>
            <h2 className="text-xl font-semibold text-foreground">
              Aucune alerte trouvée
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              {activeFiltersCount > 0 
                ? "Aucun rappel ne correspond aux critères sélectionnés. Essayez d'élargir vos critères de recherche."
                : "Aucune donnée disponible dans la base."}
            </p>
            {activeFiltersCount > 0 && (
              <div className="pt-4">
                <button
                  onClick={resetFilters}
                  className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
                >
                  Réinitialiser les filtres
                </button>
                <p className="text-sm text-muted-foreground mt-3">
                  {recalls.length} rappel{recalls.length > 1 ? 's' : ''} disponible{recalls.length > 1 ? 's' : ''} avant filtrage
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {(() => {
              let globalIndex = 0;
              return groupedRecalls.map(([dayKey, recalls]) => (
                <div key={dayKey}>
                  <DateGroupHeader date={dayKey} />
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {recalls.map((recall) => {
                      const isPriority = globalIndex < 3;
                      globalIndex++;
                      return (
                        <RecallCard
                          key={recall.id}
                          recall={recall}
                          onClick={() => navigate(`/recall/${recall.id}`)}
                          isPriority={isPriority}
                        />
                      );
                    })}
                  </div>
                </div>
              ));
            })()}
            
            {/* Load more button */}
            {hasNextPage && !barcode.isBarcode && (
              <div className="flex flex-col items-center gap-2 mt-8 pb-8">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="min-w-[200px]"
                >
                  {isFetchingNextPage ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Chargement...
                    </>
                  ) : (
                    <>Charger plus ({remainingCount} restants)</>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}